import _ from 'lodash';
import { buildCleaner } from 'lodash-clean';
import { Rarity } from '@streamloots/streamloots-types';
import {
  RewardFieldType,
  RewardField,
  PublicOriginalCardFromApi,
  PublicOriginalCard,
  PublicOriginalFrame,
} from 'model/indexTS';
import { differenceInDays } from 'date-fns';

const options = {
  isNull: _.noop,
  isUndefined: _.noop,
};
const clean = buildCleaner(options);

const getRewardFields = (rewardFields: RewardField[], useFrameAsReward: boolean, imageUrl: string) => {
  if (!useFrameAsReward) {
    return rewardFields;
  }

  return rewardFields.map(field => {
    if (field.type === RewardFieldType.ALERT) {
      return {
        ...field,
        imageUrl,
      };
    }
    return field;
  });
};

export const normalizeToPublicCard = (
  originalCard: PublicOriginalCardFromApi,
  frameId: string,
): PublicOriginalCard | null => {
  try {
    const {
      _id,
      actionType,
      autocomplete,
      description,
      descriptionDetailed,
      dropLimit,
      dropLimited,
      fragmented,
      fragments,
      frames,
      name,
      rarity,
      rarityCardProbability,
      redemptionLimit,
      redeemFields,
      redemptionSuccessMessage,
      rewardFields,
      useFrameAsReward,
      paid,
      duration,
      integrations,
    } = originalCard;
    let redemptionLimitAsDuration;

    /**
     * Why am I checking the duration is highest than 0?
     * This check is to avoid break the onboarding with this edge case.
     * I could remove the last condition, because JS understand 0 as falsy value, but as I stated, duration is a correct value on timer context and
     * I prefer to be explicit in this case.
     */
    if (redemptionLimit && duration && duration > 0) {
      const { timeFrameSeconds } = redemptionLimit?.configuration;
      if (timeFrameSeconds && timeFrameSeconds < duration) {
        redemptionLimitAsDuration = {
          configuration: {
            timeFrameSeconds: duration,
          },
        };
      }
    }

    if (!redemptionLimit && duration && duration > 0) {
      redemptionLimitAsDuration = {
        configuration: {
          timeFrameSeconds: duration,
        },
      };
    }

    const frame = _.find(frames, frameItem => frameItem.frameId === frameId) || null;

    if (!frame) {
      return null;
    }

    const { imageUrl, firstPublishedAt } = frame;

    return {
      _id,
      actionType,
      autocomplete,
      description,
      descriptionDetailed,
      dropLimit,
      dropLimited,
      duration,
      fragmented,
      fragments,
      frames,
      imageUrl,
      integrations: clean(integrations),
      name,
      paid: paid ?? false,
      rarity,
      rarityCardProbability,
      redeemable: true,
      redeemFields,
      redemptionLimit: redemptionLimitAsDuration || redemptionLimit,
      redemptionSuccessMessage,
      rewardFields: getRewardFields(rewardFields, useFrameAsReward, imageUrl),
      recentlyPublished: !!firstPublishedAt && differenceInDays(Date.now(), firstPublishedAt) < 15,
    };
  } catch (e) {
    return null;
  }
};

export const normalizeOriginals = (
  originals: PublicOriginalCardFromApi[],
  frame: PublicOriginalFrame,
): PublicOriginalCard[] => {
  const normalizedCards: PublicOriginalCard[] = [];

  originals.forEach(card => {
    const publicPredefinedCard = normalizeToPublicCard(card, frame._id);
    if (publicPredefinedCard !== null) {
      normalizedCards.push(publicPredefinedCard);
    }
  });

  return _.sortBy(normalizedCards, cardTemplate => {
    switch (cardTemplate.rarity) {
      case Rarity.LEGENDARY:
        return 0;
      case Rarity.EPIC:
        return 1;
      case Rarity.RARE:
        return 2;
      default:
        return 3;
    }
  });
};
